import MagicWand from "../../../../assets/icons/MagicWand";
import CloneIcon from "../../../../assets/icons/CloneV2";
import UnlockedIcon from "../../../../assets/icons/UnlockedIcon";
import FavouritedIcon from "../../../../assets/icons/Favourited";

// Owner-gallery wheel collections ("Wheel collections" tab strip).
//
// WHY THIS FILE EXISTS
// FoldersV2/OwnerView.jsx hardcoded `selectedCategory: "Created"` as a string
// literal on its ONE wheels request, so a cloned Wheel was unreachable in its
// owner's own gallery — while the profile stat row counts created + cloned
// (home/api/v1/viewsets.py:1375). The approved design
// (design-handoff/concepts/gallery-folders-elevated/01-owner-with-businessbox.html)
// always specified a four-tab strip; it was simply never rebuilt inside FoldersV2
// when the legacy tab UI (TabComponents/WheelsTab.jsx) was dropped.
//
// `id` is the `selectedCategory` key services/queries.js `useInfiniteWheels`
// reads from its apiUrlMap — NOT a new vocabulary:
//   Created    -> /api/v2/wheel/?mode=created   (BE filters is_cloned=False)
//   Cloned     -> /api/v2/wheel/?is_clone=true
//   Unlocked   -> /api/v2/wheel/?mode=unlock
//   Favourited -> /api/v2/wheel/?mode=favorite
// The `label` is the deck's user-facing wording (US spelling: "Favorited"),
// deliberately distinct from the backend's "Favourited" query key.
//
// `Icon` + `countKey` (#923, Gordon side-by-side vs prod 2026-08-21): the tab
// strip live on production (WheelListing/TabComponents/WheelsTab.jsx, still
// what iyuga.net's real app — web.iyuga.net — ships; confirmed via the git
// `Production` branch) pairs each tab with an icon and a live count badge.
// FoldersV2/CollectionTabs never carried either. Reusing the SAME icon
// components and the SAME `useAllTabCounts` hook WheelsTab.jsx already calls
// (services/queries.js) keeps this a true parity fix, not a new visual
// language. `countKey` is the key `useAllTabCounts` returns each count under.
export const WHEEL_COLLECTIONS = [
  {
    id: "Created",
    label: "Created",
    Icon: MagicWand,
    countKey: "created",
  },
  {
    id: "Cloned",
    label: "Cloned",
    Icon: CloneIcon,
    countKey: "cloned",
  },
  {
    id: "Unlocked",
    label: "Unlocked",
    Icon: UnlockedIcon,
    countKey: "unlocked",
  },
  {
    id: "Favourited",
    label: "Favorited",
    Icon: FavouritedIcon,
    countKey: "favorited",
  },
];

export const DEFAULT_COLLECTION = "Created";

export const isValidCollection = (id) =>
  WHEEL_COLLECTIONS.some((c) => c.id === id);

// Folders organize the wheels the OWNER created: the folder list and its
// drag & drop only ever operated on the Created set, and an Unlocked/Favorited
// wheel belongs to someone else (moving it into this owner's folder is not a
// real capability, so offering the drag would be a dead control). Cloned wheels
// are owned but are not part of the folder model today, so the folder surface
// stays exactly where it already worked. This keeps the Created tab's behaviour
// — folder row, react-dnd, folder pagination — byte-identical to before.
export const showsFolders = (collectionId) =>
  collectionId === DEFAULT_COLLECTION;

export const allowsWheelDrag = (collectionId) =>
  collectionId === DEFAULT_COLLECTION;

// Per-tab empty copy. An empty Cloned tab is NOT "you have no wheels" — each
// collection explains what would land there and how. Created keeps its existing
// first-run delight + create CTA (NoWheelsEmpty), so it is absent from this map.
export const COLLECTION_EMPTY_COPY = {
  Cloned: {
    title: "No cloned Wheels yet",
    // Renamed Explore -> Discover, Gordon 2026-08-06.
    body: "Clone a Wheel from Discover or from another creator's gallery and your copy shows up here.",
  },
  Unlocked: {
    title: "Nothing unlocked yet",
    body: "Wheels you unlock — with a password, granted access, or a purchase — collect here so you can find them again.",
  },
  Favourited: {
    title: "No favorites yet",
    body: "Favorite any Wheel you want to come back to and it will be waiting here.",
  },
};
